import { DatabaseBackup, Gauge, LogOut, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Badge, Card, PageHeader, SectionHeading } from "../components/ui";
import { useAuth } from "../lib/auth";
import { GeminiSettingsCard } from "../components/GeminiSettingsCard";
import { useWorkspace } from "../lib/workspace";

export function SettingsPage() {
  const { data } = useWorkspace();
  return data ? <SettingsForm key={data.workspace.id} initialTimezone={data.settings.timezone} /> : null;
}

function SettingsForm({ initialTimezone }: { initialTimezone: string }) {
  const { data, saveSettings } = useWorkspace();
  const { email, signOut } = useAuth();
  const [timezone, setTimezone] = useState(initialTimezone);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  if (!data) return null;

  const totals = data.usage.reduce(
    (summary, event) => ({
      events: summary.events + event.quantity,
      cost: summary.cost + event.estimatedCostUsd,
    }),
    { events: 0, cost: 0 },
  );

  return (
    <>
      <PageHeader
        title="Settings"
        description="Workspace identity, scheduling and operational status for this owner-only release."
      />

      <div className="settings-grid">
        <GeminiSettingsCard />
        <Card>
          <SectionHeading title="Owner access" description="Workspace membership is restricted to the provisioned owner. Public signup is disabled." />
          <div className="owner-card">
            <div className="avatar avatar--large">DT</div>
            <div><strong>DrTun owner</strong><p>{email ?? data.settings.ownerEmail}</p></div>
            <Badge tone="green"><ShieldCheck size={14} />Owner</Badge>
          </div>
          <button className="button button--secondary" type="button" onClick={() => void signOut()}>
            <LogOut size={17} />Sign out
          </button>
        </Card>

        <Card>
          <SectionHeading title="Scheduling" description="Displayed times use this workspace timezone." />
          <label className="field">
            <span>Timezone</span>
            <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              <option value="Asia/Bangkok">Asia/Bangkok (UTC+7)</option>
              <option value="Asia/Yangon">Asia/Yangon (UTC+6:30)</option>
              <option value="UTC">UTC</option>
            </select>
          </label>
          <p className="field-help">Dispatch time is shown first. Content becomes Published only after provider read-back confirms it.</p>
          <button className="button button--primary" type="button" disabled={saving || timezone === data.settings.timezone} onClick={() => {
            setSaving(true);
            void saveSettings(timezone, data.settings.sendsPaused).then(() => setNotice('Timezone saved.')).catch((error: unknown) => setNotice(error instanceof Error ? error.message : 'Save failed.')).finally(() => setSaving(false));
          }}>Save timezone</button>
          <p role="status">{notice}</p>
        </Card>

        <Card>
          <SectionHeading title="Usage and $10 target" description="Recorded AI tokens and sends. Cost pricing and storage metering are not yet configured." />
          <div className="settings-stat"><Gauge size={21} /><span><strong>{totals.events.toLocaleString()}</strong>units in the latest 100 recorded events</span></div>
          <div className="settings-stat"><span className="currency-icon">$</span><span><strong>${totals.cost.toFixed(4)}</strong>estimated variable cost</span></div>
          <div className="meter"><span style={{ width: `${Math.min(100, (totals.cost / 7) * 100)}%` }} /></div>
          <p className="field-help">Illustrative $7 cost allowance against the future $10 price target. Feasibility is unmeasured.</p>
        </Card>

        <Card>
          <SectionHeading title="Recovery status" description="Database and media need separate protected backups." />
          <div className="recovery-box"><DatabaseBackup size={23} /><div><strong>Restore drill required before production</strong><p>Target: at most 24 hours data loss and recovery within 4 hours. Restored sends remain paused.</p></div></div>
          <Badge tone="amber">Not live-verified</Badge>
        </Card>
      </div>
    </>
  );
}
